import React, { useEffect, useMemo, useState } from "react";
import MessengerLayout   from "../../layout/MessengerLayout";
import TicketFilters     from "../../components/tickets/TicketFilters";
import TeamTicketList    from "../../components/tickets/TeamTicketList";
import ApprovalsQueue    from "../../components/tickets/ApprovalsQueue";
import TicketDrawer      from "../../components/tickets/TicketDrawer";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";

async function apiFetch(path) {
  const token = localStorage.getItem("token");
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export default function TeamTicketsPage() {
  const [managerId, setManagerId] = useState(null);
  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "null");
      if (u?.id) setManagerId(u.id);
    } catch {}
  }, []);

  const [view,      setView]      = useState("inbox");
  const [filters,   setFilters]   = useState({
    search: "", priority: "ALL", status: "ALL",
    category: "ALL", onlySlaRisk: false, onlyAwaitingMgr: false,
    from: "", to: "",
  });

  const [tickets,   setTickets]   = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);
  const [selected,  setSelected]  = useState(null);

  useEffect(() => {
    if (!managerId) return;
    let active = true;
    setLoading(true);
    setError(null);

    const run = async () => {
      try {
        if (view === "inbox") {
          const params = new URLSearchParams({
            manager_id: managerId,
            ...(filters.priority !== "ALL" && { priority: filters.priority }),
            ...(filters.status   !== "ALL" && { status:   filters.status   }),
            ...(filters.category !== "ALL" && { category: filters.category }),
            ...(filters.from     && { from: filters.from }),
            ...(filters.to       && { to:   filters.to   }),
          });
          const data = await apiFetch(`/api/manager/tickets?${params}`);
          if (!active) return;
          setTickets(Array.isArray(data) ? data : data?.tickets || []);
        } else {
          const data = await apiFetch(`/api/tickets/approvals`);
          if (!active) return;
          setApprovals(Array.isArray(data) ? data : data?.items || []);
        }
      } catch (e) {
        if (!active) return;
        setError(e.message);
      } finally {
        if (active) setLoading(false);
      }
    };

    run();
    const handleKiraAction = (e) => {
      const t = e.detail?.action_type || "";
      if (["ticket_raise"].includes(t)) {
        run();
      }
    };
    window.addEventListener("kira_action_complete", handleKiraAction);
    return () => {
      active = false;
      window.removeEventListener("kira_action_complete", handleKiraAction);
    };
  }, [managerId, view, filters.priority, filters.status, filters.category, filters.from, filters.to]);

  // KPIs
  const kpis = useMemo(() => {
    const todayStr = new Date().toISOString().slice(0, 10);
    return {
      open:          tickets.filter(t => !["Resolved","Closed"].includes(t.status)).length,
      newToday:      tickets.filter(t => (t.created || "").slice(0, 10) === todayStr).length,
      slaRisk:       tickets.filter(t => t.sla === "AT_RISK" || t.sla === "BREACHED").length,
      awaitingMgr:   tickets.filter(t => t.awaitingManager).length,
      avgFirstResp:  "—",
      avgResolution: "—",
    };
  }, [tickets]);

  // Client-side filter
  const visibleTickets = useMemo(() => {
    let out = [...tickets];
    if (filters.onlySlaRisk)     out = out.filter(t => t.sla === "AT_RISK" || t.sla === "BREACHED");
    if (filters.onlyAwaitingMgr) out = out.filter(t => t.awaitingManager);
    if (filters.search) {
      const q = filters.search.toLowerCase();
      out = out.filter(t =>
        (t.title || "").toLowerCase().includes(q) ||
        (t.id    || "").toLowerCase().includes(q) ||
        (t.requester || "").toLowerCase().includes(q)
      );
    }
    return out;
  }, [tickets, filters]);

  const handleApprove = async (id, comment) => {
    try {
      const token = localStorage.getItem("token");
      await fetch(`${API_BASE}/api/manager/tickets/${id}/approve`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ comment }),
      });
    } finally {
      setApprovals(prev => prev.map(x => x.id === id ? { ...x, decided: "APPROVED" } : x));
    }
  };

  const handleReject = async (id, comment) => {
    try {
      const token = localStorage.getItem("token");
      await fetch(`${API_BASE}/api/manager/tickets/${id}/reject`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ comment }),
      });
    } finally {
      setApprovals(prev => prev.map(x => x.id === id ? { ...x, decided: "REJECTED" } : x));
    }
  };

  return (
    <MessengerLayout>
      <div className="p-6 text-white space-y-5">

        <div>
          <h1 className="text-2xl font-bold text-[#00ff90]">Team Tickets</h1>
          <p className="text-white/40 text-sm mt-0.5">Monitor and manage your team's support tickets</p>
        </div>

        <TicketFilters
          view={view} setView={setView}
          filters={filters} setFilters={setFilters}
          kpis={kpis}
        />

        {error && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">⚠ {error}</div>
        )}

        <div className={`${card} p-5`}>
          {view === "inbox" && (
            <TeamTicketList loading={loading} rows={visibleTickets} onRowClick={t => setSelected(t)} />
          )}
          {view === "approvals" && (
            <ApprovalsQueue loading={loading} items={approvals} onApprove={handleApprove} onReject={handleReject} />
          )}
        </div>

      </div>

      {selected && (
        <TicketDrawer
          ticket={selected}
          onClose={() => setSelected(null)}
          onUpdate={patch => {
            setTickets(prev => prev.map(t => t.id === selected.id ? { ...t, ...patch } : t));
            setSelected(s => ({ ...s, ...patch }));
          }}
        />
      )}
    </MessengerLayout>
  );
}